/**
 * OnboardingActiveContext
 *
 * Single source of truth for "chat-window onboarding is active" — the window
 * where a brand-new user (profile.onboardingComplete === false) lands in the
 * REAL app chat surface running a guided tour (the composer is LOCKED) while
 * phase-1 sync warms up. The rail is NOT hidden — it loads together with the
 * chat at shell-readiness, matching the returning-user reveal (see MainLayout /
 * App.tsx gate 5). Replaces the card-on-wood recipe onboarding
 * (OnboardingRecipeHost): onboarding now happens IN the real chat thread.
 *
 * The provider owns the LIVE `onboardingActive` boolean (seeded from the App
 * gate's `shouldUseRecipeOnboarding` result). Two consumers read it:
 *   - ChatBox / ChatComposer — lock the composer while active (the tour drives
 *     the flow); MainLayout also uses it to suppress the MOBILE conversation
 *     drawer during the tour.
 *   - useChatOnboarding (run inside ChatBox) — seeds the welcome messages,
 *     polls sync readiness, and calls `endOnboardingActive()` on ready/timeout
 *     (which ends the tour / unlocks the composer).
 *
 * Keeping the flip in one context (rather than prop-drilling a setter from App
 * down through AuthenticatedAppShell → MainLayout → ChatBox) lets the chat
 * orchestration own the transition while the layout reads the same state.
 */
import { createContext, useContext, useMemo, useState, type PropsWithChildren } from 'react'

interface OnboardingActiveContextValue {
  /** True while the chat-window onboarding tour is running (composer locked). */
  onboardingActive: boolean
  /** Flip onboardingActive → false (ends the tour; unlocks the composer). Idempotent. */
  endOnboardingActive: () => void
}

const OnboardingActiveContext = createContext<OnboardingActiveContextValue | null>(null)

export function OnboardingActiveProvider({
  initialActive,
  children,
}: PropsWithChildren<{ initialActive: boolean }>) {
  const [onboardingActive, setOnboardingActive] = useState(initialActive)

  const value = useMemo<OnboardingActiveContextValue>(
    () => ({
      onboardingActive,
      endOnboardingActive: () => setOnboardingActive(false),
    }),
    [onboardingActive],
  )

  return (
    <OnboardingActiveContext.Provider value={value}>
      {children}
    </OnboardingActiveContext.Provider>
  )
}

/**
 * Read the onboarding-active state. Safe to call outside the provider (returns
 * an inert {onboardingActive:false} default) so ChatBox/MainLayout — which also
 * render on the normal returning-user path where no provider wraps them — never
 * crash on a missing context.
 */
export function useOnboardingActive(): OnboardingActiveContextValue {
  const ctx = useContext(OnboardingActiveContext)
  if (!ctx) {
    return { onboardingActive: false, endOnboardingActive: () => {} }
  }
  return ctx
}
